import {
  Button,
  NookLogo,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@nook/ui"
import { Link, useLocation } from "@tanstack/react-router"
import { FileText, Keyboard, LayoutGrid, Search, Settings, Sparkles } from "lucide-react"
import type { MouseEvent } from "react"

import { AccountMenu } from "@/components/account-menu.js"
import { useOverlay } from "@/components/app-shell.js"
import { AskThreads } from "@/components/ask-threads.js"
import { Keys } from "@/components/keys.js"

const NAV = [
  { to: "/", label: "Library", icon: LayoutGrid },
  { to: "/notes", label: "Notes", icon: FileText },
  { to: "/ask", label: "Ask Nook", icon: Sparkles },
  { to: "/settings", label: "Settings", icon: Settings },
] as const

const HIDE_COLLAPSED = "group-data-[collapsible=icon]:hidden"

export function AppSidebar() {
  const overlay = useOverlay()
  const path = useLocation({ select: (location) => location.pathname })
  const active = (to: string) => (to === "/" ? path === "/" : path.startsWith(to))
  const { setOpenMobile } = useSidebar()
  // The phone's sheet covers the page, so any link tapped in it closes it, the current page's included.
  const onLink = (event: MouseEvent) => (event.target as Element).closest("a") && setOpenMobile(false)

  return (
    <Sidebar collapsible="icon">
      {/* On a phone `Sidebar` drops onClick (it lands on the Sheet root), so one wrapper covers every section. */}
      <div className="contents" onClick={onLink}>
        <SidebarHeader className="gap-4 px-3 pt-4">
          <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col">
            <Link to="/" className="flex items-center gap-2.5 px-2 py-1 text-[1.1875rem]">
              <NookLogo className="size-5.5 shrink-0" />
            </Link>
            <SidebarTrigger
              title="Toggle sidebar"
              className="text-muted-foreground"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setOpenMobile(false)
              overlay("palette")
            }}
            className={`text-muted-foreground h-8.5 justify-start gap-2 pr-2 pl-2.5 text-[0.8125rem] font-normal ${HIDE_COLLAPSED}`}
          >
            <Search />
            <span className="flex-1 text-left">Search</span>
          </Button>
        </SidebarHeader>

        <SidebarContent className="px-3">
          <SidebarMenu className="gap-0.5">
            {NAV.map(({ to, label, icon: Icon }) => (
              <SidebarMenuItem key={label}>
                <SidebarMenuButton
                  asChild
                  isActive={active(to)}
                  tooltip={label}
                  className="text-muted-foreground data-[active=true]:text-foreground data-[active=true]:bg-sidebar-accent h-8.5 text-[0.8125rem] data-[active=true]:font-medium"
                >
                  <Link to={to}>
                    <Icon />
                    <span className="flex-1">{label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>

          <AskThreads />
        </SidebarContent>

        <SidebarFooter className="gap-2 px-3 pb-3">
          <SidebarMenu>
            <SidebarMenuItem className="max-md:hidden">
              <SidebarMenuButton
                tooltip="Keyboard shortcuts"
                onClick={() => overlay("shortcuts")}
                className="text-muted-foreground h-8.5 text-[0.8125rem]"
              >
                <Keyboard />
                <span className="flex-1 truncate">Keyboard shortcuts</span>
                <Keys keys="[Shift]+?" className={HIDE_COLLAPSED} />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <AccountMenu />
        </SidebarFooter>
      </div>
    </Sidebar>
  )
}
